import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { H1Title, Paragraph } from "../../components/typography/typography";
import { AppButton } from "../../components/buttons/button";
import SubmissionService from "../../services/submissionService";
import JudgingService from "../../services/judgingService";
import { Success, Error as ToastError } from "../../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSave, faPaperPlane, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { getEmbedUrl } from "../../helpers/videoEmbed";

const CRITERIA = [
  { key: 'humanitarianImpact', label: 'Humanitarian Impact Potential', desc: 'Does the project address a real and significant need? How many people could it benefit, and how meaningfully?' },
  { key: 'feasibility', label: 'Feasibility', desc: 'Can this actually be implemented? Is the timeline realistic? Does the team have the capacity to execute it?' },
  { key: 'innovation', label: 'Innovation & Originality', desc: 'Is the approach creative, fresh, or an improvement on existing solutions?' },
  { key: 'clarityOfPlan', label: 'Clarity of Plan & Budget', desc: 'Is the proposal well-organized, clearly written, and backed by a realistic, justified budget?' },
];

const SCORE_LABELS = { 1: 'Poor', 2: 'Below Average', 3: 'Meets Standard', 4: 'Above Average', 5: 'Excellent' };

export default function JudgeScore() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  // Only round1/round2 are scoreable. When the query param is missing or invalid,
  // fall back to the submission's own currentRound (resolved in loadData).
  const roundParam = searchParams.get('round');
  const validParam = ['round1', 'round2'].includes(roundParam) ? roundParam : null;
  const [round, setRound] = useState(validParam);
  const navigate = useNavigate();

  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submittingScore, setSubmittingScore] = useState(false);
  const [showProposal, setShowProposal] = useState(true);

  const [scores, setScores] = useState({
    humanitarianImpact: { score: 0, notes: '' },
    feasibility: { score: 0, notes: '' },
    innovation: { score: 0, notes: '' },
    clarityOfPlan: { score: 0, notes: '' },
  });
  const [calibrationNotes, setCalibrationNotes] = useState('');
  const [panelDiscussionNotes, setPanelDiscussionNotes] = useState('');

  useEffect(() => {
    loadData();
  }, [id, validParam]);

  const loadData = async () => {
    try {
      const subRes = await SubmissionService.getSubmission(id);
      const sub = subRes.data.data.submission;
      const effectiveRound = validParam ||
        (['round1', 'round2'].includes(sub.currentRound) ? sub.currentRound : 'round1');
      setRound(effectiveRound);
      setSubmission(sub);

      const scoreRes = await JudgingService.getMyScore(id, effectiveRound).catch(() => null);
      if (scoreRes?.data?.data?.score) {
        const s = scoreRes.data.data.score;
        setScores(s.criteria);
        setCalibrationNotes(s.calibrationNotes || '');
        setPanelDiscussionNotes(s.panelDiscussionNotes || '');
      }
    } catch (err) {
      ToastError('Failed to load submission');
    } finally { setLoading(false); }
  };

  const updateScore = (criterion, field, value) => {
    setScores(prev => ({
      ...prev,
      [criterion]: { ...prev[criterion], [field]: value }
    }));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await JudgingService.saveDraftScore(id, { round, criteria: scores, calibrationNotes, panelDiscussionNotes });
      Success('Draft saved');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleSubmitScore = async () => {
    const allScored = CRITERIA.every(c => scores[c.key].score >= 1);
    if (!allScored) { ToastError('Please score all criteria before submitting'); return; }

    setSubmittingScore(true);
    try {
      await JudgingService.submitScore(id, { round, criteria: scores, calibrationNotes, panelDiscussionNotes });
      Success('Score submitted!');
      navigate('/judge/dashboard');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to submit');
    } finally { setSubmittingScore(false); }
  };

  if (loading) return <div className="py-20 text-center text-grey-667">Loading...</div>;
  if (!submission) return <div className="py-20 text-center text-grey-667">Submission not found</div>;

  const p = submission.proposal || {};
  const totalScore = CRITERIA.reduce((sum, c) => sum + (scores[c.key].score || 0), 0);
  const meetsThreshold = CRITERIA.every(c => scores[c.key].score >= 3);

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/judge/dashboard')} className="text-grey-667 hover:text-primary cursor-pointer">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div>
            <H1Title size="xs">{submission.submitterId?.firstName} {submission.submitterId?.lastName}</H1Title>
            <Paragraph size="sm" className="text-grey-667">{submission.teamId?.name || 'Individual'} &middot; {round}</Paragraph>
          </div>
        </div>
        <button onClick={() => setShowProposal(!showProposal)} className="text-sm text-primary hover:underline cursor-pointer">
          {showProposal ? 'Hide Proposal' : 'Show Proposal'}
        </button>
      </div>

      <div className={`grid ${showProposal ? 'lg:grid-cols-2' : ''} gap-6`}>
        {/* Proposal Panel */}
        {showProposal && (
          <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto client-overflow-scroll">
            {[
              { label: 'Problem Statement', value: p.problemStatement },
              { label: 'Proposed Intervention', value: p.proposedIntervention },
              { label: 'Target Population', value: p.targetPopulation },
              { label: 'Implementation Plan', value: p.implementationPlan },
            ].map((section, i) => (
              <div key={i} className="bg-white rounded-xl border border-grey-200 p-4">
                <h4 className="font-bold text-xs text-grey-dark mb-2">{section.label}</h4>
                <p className="text-sm text-grey-667 whitespace-pre-wrap">{section.value || 'Not provided'}</p>
              </div>
            ))}
            <div className="bg-white rounded-xl border border-grey-200 p-4">
              <h4 className="font-bold text-xs text-grey-dark mb-2">Budget — ${p.budget?.totalAmount?.toLocaleString() || 0}</h4>
              {p.budget?.lineItems?.map((item, i) => (
                <div key={i} className="text-sm text-grey-667 py-1 border-b border-grey-200 last:border-0">
                  {item.description}: ${parseFloat(item.amount || 0).toLocaleString()} — {item.justification}
                </div>
              ))}
            </div>
            <div className="bg-white rounded-xl border border-grey-200 p-4 space-y-2">
              <h4 className="font-bold text-xs text-grey-dark">Anticipated Outcome</h4>
              <p className="text-sm text-grey-667"><strong>Short-term:</strong> {p.anticipatedOutcome?.shortTerm || '-'}</p>
              <p className="text-sm text-grey-667"><strong>Long-term:</strong> {p.anticipatedOutcome?.longTerm || '-'}</p>
              <p className="text-sm text-grey-667"><strong>Alignment:</strong> {p.anticipatedOutcome?.problemAlignment || '-'}</p>
            </div>

            {p.supportingDocsUrl && (
              <div className="bg-white rounded-xl border border-grey-200 p-4 space-y-2">
                <h4 className="font-bold text-xs text-grey-dark">Supporting Documents</h4>
                <a href={p.supportingDocsUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-xs inline-flex items-center gap-1 break-all">
                  <FontAwesomeIcon icon={faExternalLinkAlt} /> Open document
                </a>
              </div>
            )}

            {submission.videoPitch?.url && (() => {
              const embedUrl = getEmbedUrl(submission.videoPitch.url);
              return (
                <div className="bg-white rounded-xl border border-grey-200 p-4 space-y-2">
                  <h4 className="font-bold text-xs text-grey-dark">Video Pitch</h4>
                  {embedUrl && (
                    <div className="rounded-lg overflow-hidden border border-grey-200 bg-black aspect-video">
                      <iframe src={embedUrl} className="w-full h-full" allowFullScreen allow="autoplay; encrypted-media" title="Video pitch" />
                    </div>
                  )}
                  <a href={submission.videoPitch.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-xs inline-flex items-center gap-1">
                    <FontAwesomeIcon icon={faExternalLinkAlt} /> Open in new tab
                  </a>
                </div>
              );
            })()}
          </div>
        )}

        {/* Scoring Panel */}
        <div className="space-y-4">
          {/* Score Summary */}
          <div className={`rounded-xl border p-4 text-center ${meetsThreshold && totalScore > 0 ? 'bg-green-light border-green/20' : totalScore > 0 ? 'bg-red-500/5 border-red-500/20' : 'bg-white border-grey-200'}`}>
            <p className="text-3xl font-bold">{totalScore}/20</p>
            <p className="text-xs text-grey-667">
              {totalScore === 0 ? 'Not scored yet' : meetsThreshold ? 'Meets minimum threshold' : 'Below threshold (needs 3+ on all)'}
            </p>
          </div>

          {/* Criteria */}
          {CRITERIA.map((c) => (
            <div key={c.key} className="bg-white rounded-xl border border-grey-200 p-4">
              <h4 className="font-bold text-sm mb-1">{c.label}</h4>
              <p className="text-xs text-grey-98 mb-3">{c.desc}</p>
              <div className="flex gap-2 mb-3">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    onClick={() => updateScore(c.key, 'score', n)}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-bold cursor-pointer transition-colors ${
                      scores[c.key].score === n
                        ? n >= 3 ? 'bg-primary text-white' : 'bg-red-500 text-white'
                        : 'bg-grey-bg text-grey-667 hover:bg-grey-200'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              {scores[c.key].score > 0 && (
                <p className="text-xs text-grey-98 mb-2">{SCORE_LABELS[scores[c.key].score]}</p>
              )}
              <textarea
                className="w-full border border-grey-200 rounded-md p-3 text-sm outline-none focus:border-primary resize-y min-h-[60px]"
                placeholder="Notes (optional)"
                value={scores[c.key].notes}
                onChange={(e) => updateScore(c.key, 'notes', e.target.value)}
              />
            </div>
          ))}

          {/* Calibration & Panel Notes */}
          <div className="bg-white rounded-xl border border-grey-200 p-4 space-y-3">
            <div>
              <label className="block text-sm font-semibold mb-1">Calibration Notes</label>
              <textarea className="w-full border border-grey-200 rounded-md p-3 text-sm outline-none focus:border-primary resize-y min-h-[60px]" placeholder="Notes from calibration session..." value={calibrationNotes} onChange={(e) => setCalibrationNotes(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Panel Discussion Notes</label>
              <textarea className="w-full border border-grey-200 rounded-md p-3 text-sm outline-none focus:border-primary resize-y min-h-[60px]" placeholder="Notes from panel discussion..." value={panelDiscussionNotes} onChange={(e) => setPanelDiscussionNotes(e.target.value)} />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <AppButton onClick={handleSaveDraft} variant="outline" fullWidth loading={saving}>
              <FontAwesomeIcon icon={faSave} className="mr-2" /> Save Draft
            </AppButton>
            <AppButton onClick={handleSubmitScore} fullWidth loading={submittingScore}>
              <FontAwesomeIcon icon={faPaperPlane} className="mr-2" /> Submit Score
            </AppButton>
          </div>
        </div>
      </div>
    </div>
  );
}
